import React, { useState } from 'react';
import type { HilPresentationAction } from '@bizfirst/hil-types';

interface HilActionBarProps {
  actions: HilPresentationAction[];
  onAction: (action: HilPresentationAction) => void;
  disabled?: boolean;
}

function resolveStyle(action: HilPresentationAction): string {
  const style = action.Style?.toLowerCase();
  if (style === 'danger' || style === 'destructive') return 'hil-action-btn hil-action-btn--danger';
  if (style === 'warning') return 'hil-action-btn hil-action-btn--warning';
  if (style === 'success' || style === 'submit') return 'hil-action-btn hil-action-btn--success';
  if (style === 'primary' || action.IsPrimary) return 'hil-action-btn hil-action-btn--primary';
  const label = resolveLabel(action)?.toLowerCase();
  if (label === 'submit') return 'hil-action-btn hil-action-btn--success';
  return 'hil-action-btn hil-action-btn--neutral';
}

function resolveLabel(action: HilPresentationAction): string {
  // Backend sends Label; frontend code historically used ActionLabel.
  return action.Label ?? action.ActionLabel ?? action.ActionKey;
}

function sortByDisplayOrder(a: HilPresentationAction, b: HilPresentationAction): number {
  return (a.DisplayOrder ?? 999) - (b.DisplayOrder ?? 999);
}

export function HilActionBar({ actions, onAction, disabled }: HilActionBarProps): React.JSX.Element {
  const sorted = [...actions].sort(sortByDisplayOrder);

  // Pending action waiting for inline input (RequiresInput=true)
  const [pendingAction, setPendingAction] = useState<HilPresentationAction | null>(null);
  const [inlineInput, setInlineInput] = useState('');

  const handleClick = (action: HilPresentationAction) => {
    if (action.RequiresInput) {
      setPendingAction(action);
      setInlineInput('');
    } else {
      onAction(action);
    }
  };

  const confirmInlineInput = () => {
    if (!pendingAction) return;
    if (pendingAction.InputRequired && !inlineInput.trim()) return;
    // Attach the inline input to the action as InputValue before firing
    onAction({ ...pendingAction, Config: { ...pendingAction.Config, inputValue: inlineInput } });
    setPendingAction(null);
    setInlineInput('');
  };

  const cancelInlineInput = () => {
    setPendingAction(null);
    setInlineInput('');
  };

  return (
    <div className="hil-action-bar">
      {pendingAction ? (
        <div className="hil-inline-input-group">
          <label className="hil-label" htmlFor="hil-inline-input">
            {pendingAction.InputLabel ?? resolveLabel(pendingAction)}
            {pendingAction.InputRequired && ' *'}
          </label>
          <input
            id="hil-inline-input"
            className="hil-input"
            type="text"
            value={inlineInput}
            onChange={(e) => setInlineInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmInlineInput(); if (e.key === 'Escape') cancelInlineInput(); }}
            autoFocus
          />
          <div className="hil-inline-input-actions">
            <button
              className="hil-action-btn hil-action-btn--neutral"
              onClick={cancelInlineInput}
            >
              Cancel
            </button>
            <button
              className={resolveStyle(pendingAction)}
              disabled={pendingAction.InputRequired && !inlineInput.trim()}
              onClick={confirmInlineInput}
            >
              {resolveLabel(pendingAction)}
            </button>
          </div>
        </div>
      ) : (
        sorted.map((action) => {
          const isDisabled = disabled || action.Enabled === false;
          return (
            <button
              key={action.ActionKey}
              className={resolveStyle(action)}
              disabled={isDisabled}
              title={action.Description}
              onClick={() => handleClick(action)}
            >
              {resolveLabel(action)}
            </button>
          );
        })
      )}
    </div>
  );
}
